import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Heart, MessageCircleX, Hand, UserCheck, Flag, AlertTriangle, ChevronRight, Lock, BookOpen } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { BackToTopButton, ContentNavFooter } from '../components/NavigationHelpers';
import { Button } from '../components/ui/button';
import { authFetch } from '../lib/authFetch';
import { useAuth } from '../contexts/AuthContext';
import useTheme4A from '../components/useTheme4A';

const SECTIONS = [
  {
    id: 'thirteenth-step',
    icon: Shield,
    color: 'rose',
    title: 'The 13th Step: what it is, why we forbid it',
    body: (
      <>
        <p className="mb-3">
          The "13th Step" is the unspoken term for when an experienced member of a Recovery community
          (almost always a man) approaches a newcomer (almost always a woman) sexually or romantically.
          It is harmful because:
        </p>
        <ul className="list-disc ml-5 space-y-1.5 mb-3">
          <li>The newcomer's nervous system is still flooded with shame, fear and survival hormones from active addiction.</li>
          <li>The power dynamic is uneven: sober time is often mistaken for emotional safety.</li>
          <li>Sexual entanglement in early Recovery is a leading cause of relapse for women.</li>
        </ul>
        <p className="mb-3 font-semibold text-zinc-800">Inside Axiom ARP, 13th-Stepping is treated as a moderation violation.</p>
        <p>
          If a man in the Recovery Room flirts with you, asks for your number, comments on your appearance,
          DMs you privately or escalates pace: flag the message and tell a SuperAdmin immediately. You do
          not owe him politeness, an explanation or a "let him down easy" reply.
        </p>
      </>
    ),
  },
  {
    id: 'no-complete-sentence',
    icon: Hand,
    color: 'amber',
    title: '"No" is a complete sentence',
    body: (
      <>
        <p className="mb-3">
          Many women who arrive in Recovery have learnt: through trauma, through people-pleasing patterns,
          through fear: that "no" must be cushioned with apologies, explanations or invented reasons.
          It does not.
        </p>
        <p className="mb-3 font-semibold text-zinc-800">Practise these full sentences. Use them out loud:</p>
        <ul className="list-none space-y-2 mb-3">
          <li className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900">"No."</li>
          <li className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900">"No, thank you."</li>
          <li className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900">"That doesn't work for me."</li>
          <li className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900">"I'm not available for that."</li>
          <li className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-900">"I've answered. We're not discussing it again."</li>
        </ul>
        <p>
          You are allowed to repeat the same one-line "no" as many times as needed. You are allowed to walk
          away mid-conversation. You are allowed to block, mute or leave a meeting. None of this is rude.
          It is sober behaviour.
        </p>
      </>
    ),
  },
  {
    id: 'sexual-abstinence',
    icon: Heart,
    color: 'violet',
    title: 'Sexual abstinence in early Recovery',
    body: (
      <>
        <p className="mb-3">
          A widely-supported clinical guideline is that women in their first <strong>year</strong> of
          Recovery avoid new romantic or sexual relationships. The rationale is medical, not moral:
        </p>
        <ul className="list-disc ml-5 space-y-1.5 mb-3">
          <li>The brain's reward pathway is still re-regulating dopamine after substance use: sexual intensity can mimic the "high" and trigger relapse.</li>
          <li>Choosing partners while emotionally raw, lonely and shame-loaded almost always replicates the same dynamics that drove drinking/using.</li>
          <li>Recovery in the first year is identity work: you are learning who you are without the substance. A new partner short-circuits that.</li>
        </ul>
        <p className="mb-3">
          Existing committed relationships are different: work those through with your therapist or sponsor.
          The guideline above is specifically about <em>starting</em> something new while you are early.
        </p>
        <p className="text-[12px] text-zinc-500 italic">
          This is a recommendation, not a prescription. Some women's circumstances differ: talk to a clinician
          or experienced same-gender sponsor about what is right for you.
        </p>
      </>
    ),
  },
  {
    id: 'boundaries',
    icon: Shield,
    color: 'emerald',
    title: 'Boundaries 101: what they actually are',
    body: (
      <>
        <p className="mb-3">
          A boundary is not a rule you impose on someone else. It is a statement of what
          <strong> you will do</strong> if a behaviour continues.
        </p>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
          <p className="text-[12px] uppercase tracking-wider font-bold text-emerald-700 mb-2">Examples</p>
          <ul className="space-y-2 text-zinc-800">
            <li>❌ "Stop texting me at midnight."  → that's a request, not a boundary.</li>
            <li>✓ "If you text me after 9pm, I won't reply until the next morning." → that's a boundary.</li>
            <li>❌ "Don't comment on my body."  → request.</li>
            <li>✓ "If you comment on my body again, I'll leave the room and report it." → boundary.</li>
          </ul>
        </div>
        <p>
          Four useful boundary categories: <strong>physical</strong> (touch, proximity), <strong>emotional</strong>{' '}
          (what you'll engage with), <strong>time</strong> (when you respond / are available),{' '}
          <strong>digital</strong> (DMs, social media follows, sharing locations).
        </p>
      </>
    ),
  },
  {
    id: 'sponsor-selection',
    icon: UserCheck,
    color: 'sky',
    title: 'Same-gender sponsorship: why it matters',
    body: (
      <>
        <p className="mb-3">
          The strong recommendation across Recovery fellowships worldwide is that women sponsor women and men
          sponsor men. This is not about distrust: it is about clarity.
        </p>
        <ul className="list-disc ml-5 space-y-1.5 mb-3">
          <li>Sponsorship requires complete honesty about sex, secrets and shame. Cross-gender sponsorship adds an unnecessary layer of self-monitoring.</li>
          <li>It keeps the relationship grounded in Recovery, not romance.</li>
          <li>It protects both people from rumour and from genuine boundary-blur.</li>
        </ul>
        <p>
          Inside Axiom ARP your <strong>AI Sponsor Bot</strong> defaults to your own gender automatically when
          you complete Personal History. You can change this in Settings, but the default is intentional.
        </p>
      </>
    ),
  },
  {
    id: 'reporting',
    icon: Flag,
    color: 'rose',
    title: 'Reporting inappropriate behaviour',
    body: (
      <>
        <p className="mb-3">
          If anyone in the Recovery Room: or anywhere in the app: behaves inappropriately toward you, here's
          exactly what to do:
        </p>
        <ol className="list-decimal ml-5 space-y-2 mb-3">
          <li>
            <strong>Flag the message.</strong> Tap the small flag icon on any message that bothers you.
            Two unique flaggers automatically escalates to SuperAdmin review.
          </li>
          <li>
            <strong>Do not engage.</strong> You are not obligated to "explain why" or "give him a chance."
            Block him in the room (coming soon) or ignore the message.
          </li>
          <li>
            <strong>Tell a SuperAdmin.</strong> SuperAdmins can suspend an account from the chat for a fixed
            period (24 hours and up) while they investigate and permanently ban for repeat offences.
          </li>
        </ol>
        <p className="mb-3">
          You will <strong>never</strong> be asked to confront the offender directly and your identity is
          protected by your Recovery Room pseudonym.
        </p>
        <p className="text-[12px] text-zinc-500 italic">
          If something feels physically threatening: in-person at a meeting or off-platform: call your country's
          emergency services first, then tell a SuperAdmin.
        </p>
      </>
    ),
  },
];

const COLOR = {
  rose:    { bg: 'bg-rose-100',    text: 'text-rose-700',    ring: 'ring-rose-200' },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  violet:  { bg: 'bg-violet-100',  text: 'text-violet-700',  ring: 'ring-violet-200' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  sky:     { bg: 'bg-sky-100',     text: 'text-sky-700',     ring: 'ring-sky-200' },
};

function WomenOnlyPage() {
  useTheme4A();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [access, setAccess] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/women-only/access`);
        const d = await r.json();
        if (!cancelled) setAccess({ ...d, loading: false });
      } catch (e) {
        if (!cancelled) setAccess({ allowed: false, reason: 'error', loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (access.loading) {
    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title="Women Only" />
        <div className="pt-24 px-6 text-center text-sm text-zinc-500">Checking access…</div>
      </div>
    );
  }

  if (!access.allowed) {
    const reasonText = {
      personal_history_required: "Please complete your Personal History first: we use the gender field there to confirm access.",
      female_only: "This area is for women only. Men: same-gender support material will be added in a future release.",
      error: "Could not verify access. Please try again in a moment.",
    }[access.reason] || "This area is restricted.";

    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title="Women Only" />
        <main className="pt-20 px-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center" data-testid="women-only-blocked">
              <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-3">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-base font-bold text-zinc-900 mb-2">Women-only safe space</h2>
              <p className="text-[13px] text-zinc-600 leading-relaxed mb-5">{reasonText}</p>
              {access.reason === 'personal_history_required' ? (
                <Button onClick={() => navigate('/personal-history')} className="w-full bg-zinc-950 text-white hover:bg-zinc-800">
                  Open Personal History
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full">
                  Back to Dashboard
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      <PageHeader title="Women Only" />
      <BackToTopButton />

      <main className="pt-20 pb-20 px-4">
        <div className="max-w-md mx-auto space-y-4">

          {/* Hero */}
          <div className="bg-white border border-rose-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-zinc-900 leading-tight">A safe space for women in Recovery</h2>
                <p className="text-[12px] text-zinc-600 mt-1 leading-relaxed">
                  Material here exists to keep you protected, grounded and unhurried while your nervous system rebuilds.
                  None of it is optional reading.
                </p>
              </div>
            </div>
            {access.reason === 'superadmin_review' && (
              <div className="mt-3 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded-md text-[11px] text-zinc-600">
                <AlertTriangle className="w-3.5 h-3.5 inline-block mr-1 text-zinc-500" />
                You are viewing as SuperAdmin (read-only moderation review).
              </div>
            )}
          </div>

          {/* Quick TOC */}
          <div className="bg-white border border-zinc-200 rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 px-2 pt-1 pb-2">In this section</p>
            <div className="space-y-1">
              {SECTIONS.map((s) => {
                const c = COLOR[s.color] || COLOR.rose;
                const Icon = s.icon;
                return (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-zinc-50 transition-colors"
                    data-testid={`women-toc-${s.id}`}
                  >
                    <div className={`w-7 h-7 rounded-md ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[12px] text-zinc-800 flex-1">{s.title}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Sections */}
          {SECTIONS.map((s) => {
            const c = COLOR[s.color] || COLOR.rose;
            const Icon = s.icon;
            return (
              <article
                id={s.id}
                key={s.id}
                className="bg-white border border-zinc-200 rounded-xl p-5 scroll-mt-24"
                data-testid={`women-section-${s.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-9 h-9 rounded-lg ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="text-sm font-bold text-zinc-900">{s.title}</h3>
                </div>
                <div className="text-[13px] text-zinc-700 leading-relaxed">{s.body}</div>
              </article>
            );
          })}

          {/* Closing: keep it human */}
          <div className="bg-zinc-950 text-zinc-100 rounded-xl p-5">
            <p className="text-[13px] leading-relaxed mb-3">
              You arrived here for a reason. Stay close to other women in Recovery, hold the line on your boundaries
              even when it feels rude and remember that nothing about your past disqualifies you from a sober future.
            </p>
            <p className="text-[12px] text-zinc-400 italic">The Axiom ARP team</p>
          </div>

          <ContentNavFooter />
        </div>
      </main>
    </div>
  );
}

export default WomenOnlyPage;
